import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

// Section cards reuse the Case Study look (CaseSectionStack / CaseSection / CaseSectionLabel).

export const LabChipRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

export const LabChip = styled(Box)`
  padding: 3px 12px;
  border-radius: 999px;
  border: 1.5px solid #0097B2;
  color: #0097B2;
  font-size: 13px;
  font-weight: 700;
`;

export const LabStepList = styled('ol')`
  li + li {
    margin-top: 12px;
  }
`;

export const LabMuted = styled(Box)`
  font-size: 15px;
  color: var(--brand-text-muted, #6B7280);
`;

// The question a learner answers before running a step: a muted line with an accent bar.
export const LabPredict = styled(LabMuted)`
  margin: 4px 0;
  padding: 4px 10px;
  border-left: 3px solid #0097B2;
  background: #F0FAFB;
`;

// One artifact: a label row (kind · language, copy button) over a plain-text code block.
export const LabArtifact = styled(Box)`
  margin-top: 8px;
  border: 1px solid #1F2937;
  border-radius: 8px;
  overflow: hidden;

  pre {
    margin: 0;
    padding: 12px 14px;
    background: #111827;
    color: #F9FAFB;
    font-size: 14px;
    line-height: 1.5;
    overflow-x: auto;
  }
`;

// Reading and writing material (a sample, a template, a checklist, a prompt draft): a light page, not code.
export const LabDocArtifact = styled(LabArtifact)`
  border-color: #CBD5E1;

  pre {
    background: #FFFFFF;
    color: #1F2937;
    font-size: 16px;
    white-space: pre-wrap;
  }

  pre,
  pre code {
    font-family: inherit;
  }

  & > div:first-of-type {
    background: #F1F5F9;
    color: #475569;
  }

  & > div:first-of-type button {
    background: #E2E8F0;
    color: #1F2937;
  }
`;

// Answers to a judging task: closed until the learner opens it (works in the static HTML deck too).
export const LabAnswerKey = styled('details')`
  margin-top: 8px;
  border: 1px dashed #0097B2;
  border-radius: 8px;
  padding: 8px 12px;
  background: #F0FAFB;

  summary {
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    color: #00687A;
  }

  pre {
    margin: 8px 0 0;
    font-family: inherit;
    font-size: 16px;
    white-space: pre-wrap;
  }
`;

// The part of a step the learner writes in their own words.
export const LabWrites = styled(LabPredict)`
  border-left-color: #F28C28;
  background: #FFF7ED;
`;

export const LabArtifactBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  background: #1F2937;
  color: #D1D5DB;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  button {
    border: 0;
    border-radius: 4px;
    padding: 2px 10px;
    background: #374151;
    color: #F9FAFB;
    font-size: 12px;
    cursor: pointer;
  }
`;

export const LabKind = styled('span')`
  margin-left: 8px;
  font-size: 12px;
  color: var(--brand-text-muted, #6B7280);
`;

// Where a step happens (terminal, code editor, ...), shown before the step action.
export const LabWhere = styled('span')`
  display: inline-block;
  margin-right: 8px;
  padding: 1px 8px;
  border-radius: 4px;
  background: #E0F4F7;
  color: #00687A;
  font-size: 12px;
  font-weight: 700;
  vertical-align: middle;
`;

// A list without bullets whose items start with their own marker (a file path, a checkbox).
export const LabPlainList = styled('ul')`
  && {
    list-style: none;
    padding-left: 0;
  }

  code {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 15px;
    padding: 1px 6px;
    border-radius: 4px;
    background: #F3F4F6;
  }
`;
